import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemIcon, ListItemText, ListItemButton, Divider } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuoteLeft, faGlobe, faBrain } from '@fortawesome/free-solid-svg-icons';
import CitationSettings from '../components/settings/CitationSettings';
import AllowedDomainsSettings from '../components/settings/AllowedDomainsSettings';
import RAGPreferencesSettings from '../components/settings/RAGPreferencesSettings';
import { getCitationSettings } from '../services/api';
import Loader from '../components/common/Loader';

interface SettingsSection {
  id: string;
  label: string;
  icon: any;
  component: React.ReactNode;
}

const SettingsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('citations');
  const [citationsEnabled, setCitationsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const clientId = localStorage.getItem('client_id') || '';

  useEffect(() => {
    checkCitationStatus();
    
    // Listen for citation setting changes
    const handleCitationSettingsChanged = () => {
      checkCitationStatus();
    };
    
    window.addEventListener('citation-settings-changed', handleCitationSettingsChanged);
    
    return () => {
      window.removeEventListener('citation-settings-changed', handleCitationSettingsChanged);
    };
  }, []);

  const checkCitationStatus = async () => {
    try {
      setLoading(true);
      const data = await getCitationSettings(clientId);
      
      if (data.success) {
        setCitationsEnabled(data.citations_enabled);
      }
    } catch (error) {
      console.error("Error checking citation status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCitationStatusChange = (enabled: boolean) => {
    setCitationsEnabled(enabled);
    console.log("Citation status changed to:", enabled);
  };

  // Define settings sections
  const settingsSections: SettingsSection[] = [
    {
      id: 'citations',
      label: 'Citations',
      icon: faQuoteLeft,
      component: <CitationSettings clientId={clientId} onStatusChange={handleCitationStatusChange} />
    },
    {
      id: 'allowed-domains',
      label: 'Allowed Domains',
      icon: faGlobe,
      component: <AllowedDomainsSettings clientId={clientId} />
    },
    {
      id: 'rag-preferences',
      label: 'Source Preferences',
      icon: faBrain,
      component: <RAGPreferencesSettings clientId={clientId} />
    }
  ];

  if (loading) {
    return (
      <div className="h-full min-h-[400px] relative">
        <Loader 
          fullPage={false} 
          text="Loading settings..." 
          variant="pulse"
          overlay={true} 
        />
      </div>
    );
  }

  // Find the current active section component
  const activeComponent = settingsSections.find(section => section.id === activeSection)?.component;

  return (
    <div className="flex flex-col md:flex-row gap-4 p-4">
      {/* Left Navigation Panel */}
      <Paper className="w-full md:w-72 flex-shrink-0 bg-white rounded-lg shadow overflow-hidden">
        <Box className="p-4 bg-gray-50 border-b">
          <Typography variant="h6" className="text-gray-800 font-medium">Settings</Typography>
        </Box>
        <List component="nav" className="py-2">
          {settingsSections.map((section, index) => (
            <React.Fragment key={section.id}>
              {index > 0 && <Divider variant="middle" className="my-1" />}
              <ListItem disablePadding>
                <ListItemButton 
                  selected={activeSection === section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={activeSection === section.id ? "bg-blue-50" : ""}
                >
                  <ListItemIcon className={activeSection === section.id ? "text-blue-600" : "text-gray-500"}>
                    <FontAwesomeIcon icon={section.icon} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={section.label} 
                    primaryTypographyProps={{ 
                      className: activeSection === section.id ? "text-blue-600 font-medium" : "text-gray-700"
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Content Area */}
      <Paper className="bg-white rounded-lg shadow w-full p-6">
        {activeComponent}
      </Paper>
    </div>
  );
};

export default SettingsPage; 